import React, { useState } from "react";
import { useList, useUsersMap, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, StatusBadge, EmptyState, Field, money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Plus, Buildings, PencilSimple, Trash, CaretDown, CaretRight, SteeringWheel, Truck, NotePencil, SignOut, UserSwitch } from "@phosphor-icons/react";
import { EQUIPMENT_TYPES, PAYMENT_METHODS } from "@/lib/constants";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { OffboardDialog } from "@/components/OffboardDialog";
import { FmcsaLookup } from "@/components/FmcsaLookup";
import { RevenueRanking } from "@/components/RevenueRanking";
import { ChangeDispatcher } from "@/components/ChangeDispatcher";

const empty = {
  carrier_name: "", mc_number: "", usdot: "", ein: "", email: "", phone: "", address: "",
  assigned_dispatcher: "", source: "", payment_method: "", dispatch_charges_rate: "",
  additional_service: "", lead_born_date: "", first_load_date: "", status: "active",
  fleet: [], notes: "",
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function ActiveCarriers() {
  const { data: carriers = [], isLoading } = useList("carriers", "/carriers");
  const { users } = useUsersMap();
  const dispatchers = users.filter((u) => u.role === "dispatcher");
  const [reassign, setReassign] = useState(null);
  const invalidate = useInvalidate();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [offboarding, setOffboarding] = useState(null);
  const { user } = useAuth();

  const fleet = form.fleet || [];
  const fleetQty = (t) => fleet.find((x) => x.type === t)?.qty || 0;
  const setFleetQty = (t, v) => {
    const qty = Math.max(0, parseInt(v) || 0);
    let next = fleet.filter((x) => x.type !== t);
    if (qty > 0) next = [...next, { type: t, qty }];
    setForm((f) => ({ ...f, fleet: next }));
  };
  const totalTrucks = fleet.reduce((s, x) => s + (parseInt(x.qty) || 0), 0);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c, assigned_dispatcher: c.assigned_dispatcher || "" }); setOpen(true); };

  const save = async () => {
    if (!form.carrier_name.trim()) { toast.error("Carrier name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, assigned_dispatcher: form.assigned_dispatcher || null };
      if (editing) await api.put(`/carriers/${editing.id}`, payload);
      else await api.post("/carriers", payload);
      toast.success(editing ? "Carrier updated" : "Carrier added");
      setOpen(false); invalidate("carriers");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this carrier?")) return;
    await api.delete(`/carriers/${id}`);
    invalidate("carriers"); toast.success("Carrier removed");
  };

  const inp = "bg-white border-slate-300";

  return (
    <div>
      <PageHeader title="Active Carriers" subtitle="Every onboarded carrier with live revenue and assigned drivers"
        actions={<Button data-testid="add-carrier-btn" onClick={openNew} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold"><Plus size={18} weight="bold" className="mr-1.5" />Add Carrier</Button>} />
      {user?.role === "admin" && <RevenueRanking />}

      <OffboardDialog carrier={offboarding} onClose={() => setOffboarding(null)} onDone={() => invalidate(["carriers", "offboarded-carriers"])} />
      <ChangeDispatcher carrier={reassign} dispatchers={dispatchers} onClose={() => setReassign(null)} onSaved={() => invalidate("carriers")} />
      <Panel className="p-0">
        {isLoading ? <p className="p-6 text-slate-400">Loading…</p> : carriers.length === 0 ? (
          <EmptyState icon={Buildings} title="No active carriers" hint="Add your first carrier to start tracking revenue and drivers." action={<Button onClick={openNew} className="bg-blue-600 text-white hover:bg-blue-700"><Plus size={16} className="mr-1" />Add Carrier</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Carrier</TableHead>
                  <TableHead className="hidden lg:table-cell">Dispatcher</TableHead>
                  <TableHead className="hidden md:table-cell">MC / USDOT</TableHead>
                  <TableHead className="hidden xl:table-cell">Payment</TableHead>
                  <TableHead className="hidden xl:table-cell">Charges</TableHead>
                  <TableHead>Total Load Amount</TableHead>
                  {user?.role === "admin" && <TableHead>Dispatch Charges</TableHead>}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {carriers.map((c, i) => (
                  <React.Fragment key={c.id}>
                    <TableRow className="border-slate-100 hover:bg-slate-50" data-testid={`carrier-row-${c.id}`}>
                      <TableCell className="text-slate-400 font-mono text-sm">{i + 1}</TableCell>
                      <TableCell>
                        <button onClick={() => setExpanded(expanded === c.id ? null : c.id)} className="flex items-center gap-1.5 text-left" data-testid={`carrier-expand-${c.id}`}>
                          {expanded === c.id ? <CaretDown size={14} className="text-slate-400" /> : <CaretRight size={14} className="text-slate-400" />}
                          <div>
                            <p className="font-semibold text-slate-900">{c.carrier_name}</p>
                            <p className="text-xs text-slate-500">{c.email || c.phone || "—"} · {c.drivers?.length || 0} drivers</p>
                          </div>
                        </button>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-slate-600">{c.assigned_dispatcher_name}</TableCell>
                      <TableCell className="hidden md:table-cell font-mono text-xs text-slate-600">{c.mc_number || "—"} / {c.usdot || "—"}</TableCell>
                      <TableCell className="hidden xl:table-cell text-sm text-slate-600">{c.payment_method || "—"}</TableCell>
                      <TableCell className="hidden xl:table-cell text-sm text-slate-600">{c.dispatch_charges_rate || "—"}</TableCell>
                      <TableCell><span className="font-mono font-semibold text-emerald-600" data-testid={`carrier-revenue-${c.id}`}>{money(c.total_load_amount)}</span><span className="block text-[11px] text-slate-400">{c.in_transit_loads || 0} in transit · {c.delivered_loads || 0} delivered</span></TableCell>
                      {user?.role === "admin" && <TableCell><span className="font-mono font-semibold text-blue-600" data-testid={`carrier-dispatch-revenue-${c.id}`}>{money(c.dispatch_revenue)}</span></TableCell>}
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => openEdit(c)} className="p-1.5 text-slate-500 hover:text-blue-600" data-testid={`edit-carrier-${c.id}`}><PencilSimple size={16} /></button>
                          {user?.role === "admin" && <button onClick={() => setReassign(c)} className="p-1.5 text-slate-500 hover:text-blue-600" title="Change Dispatcher" data-testid={`change-dispatcher-${c.id}`}><UserSwitch size={16} /></button>}
                          {user?.role === "admin" && <button onClick={() => setOffboarding(c)} className="p-1.5 text-slate-500 hover:text-amber-500" title="Offboard Carrier" data-testid={`offboard-carrier-${c.id}`}><SignOut size={16} /></button>}
                          <button onClick={() => remove(c.id)} className="p-1.5 text-slate-500 hover:text-red-400" data-testid={`delete-carrier-${c.id}`}><Trash size={16} /></button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expanded === c.id && (
                      <TableRow className="border-slate-100 bg-slate-50/60">
                        <TableCell />
                        <TableCell colSpan={7} className="py-3">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 text-sm mb-3">
                            <div><span className="text-slate-400 text-xs">Address</span><p className="text-slate-700">{c.address || "—"}</p></div>
                            <div><span className="text-slate-400 text-xs">Source</span><p className="text-slate-700">{c.source || "—"}</p></div>
                            <div><span className="text-slate-400 text-xs">Lead Born</span><p className="text-slate-700">{fmtDate(c.lead_born_date)}</p></div>
                            <div><span className="text-slate-400 text-xs">First Load</span><p className="text-slate-700">{fmtDate(c.first_load_date)}</p></div>
                            <div className="col-span-2"><span className="text-slate-400 text-xs">Additional Service</span><p className="text-slate-700">{c.additional_service || "—"}</p></div>
                          </div>
                          {(c.fleet?.length > 0 || c.notes) && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
                              {c.fleet?.length > 0 && (
                                <div className="rounded-lg border border-slate-200 bg-white p-3">
                                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><Truck size={14} />Fleet · {c.fleet.reduce((s, x) => s + (parseInt(x.qty) || 0), 0)} trucks</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {c.fleet.map((f, idx) => <span key={idx} className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold px-2.5 py-1 ring-1 ring-blue-200">{f.type} <b>×{f.qty}</b></span>)}
                                  </div>
                                </div>
                              )}
                              {c.notes && (
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                  <p className="text-xs font-bold uppercase tracking-wide text-amber-600 mb-1 flex items-center gap-1.5"><NotePencil size={14} />Dispatcher Notes</p>
                                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.notes}</p>
                                </div>
                              )}
                            </div>
                          )}
                          <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 flex items-center gap-1.5"><SteeringWheel size={14} />Assigned Drivers</p>
                          {c.drivers?.length ? (
                            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                              <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-slate-500 text-xs"><tr><th className="text-left px-3 py-1.5">Driver</th><th className="text-left px-3 py-1.5">Number</th><th className="text-left px-3 py-1.5">Truck #</th><th className="text-left px-3 py-1.5">Trailer #</th><th className="text-left px-3 py-1.5">VIN</th></tr></thead>
                                <tbody className="divide-y divide-slate-100">
                                  {c.drivers.map((d) => (
                                    <tr key={d.id} data-testid={`carrier-driver-${d.id}`}>
                                      <td className="px-3 py-1.5 font-medium text-slate-800">{d.name}</td>
                                      <td className="px-3 py-1.5 font-mono text-slate-600">{d.phone || "—"}</td>
                                      <td className="px-3 py-1.5 font-mono text-slate-600">{d.truck || "—"}</td>
                                      <td className="px-3 py-1.5 font-mono text-slate-600">{d.trailer || "—"}</td>
                                      <td className="px-3 py-1.5 font-mono text-slate-600">{d.vin || "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : <p className="text-sm text-slate-400">No drivers assigned. Assign drivers in Dispatch → Driver Info.</p>}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Carrier" : "Add Carrier"}</DialogTitle><DialogDescription className="sr-only">Carrier details form</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="sm:col-span-2"><FmcsaLookup testid="carrier-fmcsa" onSelect={(r) => setForm((f) => ({ ...f, carrier_name: r.legal_name || f.carrier_name, mc_number: r.mc_number || f.mc_number, usdot: r.usdot || f.usdot, email: r.email || f.email, phone: r.phone || f.phone, address: r.address || f.address }))} /></div>
            <div className="sm:col-span-2"><Field label="Carrier Name *"><Input data-testid="carrier-name" value={form.carrier_name} onChange={(e) => setForm({ ...form, carrier_name: e.target.value })} className={inp} /></Field></div>
            <Field label="MC #"><Input data-testid="carrier-mc" value={form.mc_number} onChange={(e) => setForm({ ...form, mc_number: e.target.value })} className={`${inp} font-mono`} /></Field>
            <Field label="USDOT #"><Input value={form.usdot} onChange={(e) => setForm({ ...form, usdot: e.target.value })} className={`${inp} font-mono`} data-testid="carrier-usdot" /></Field>
            <Field label="EIN (Federal Tax ID)"><Input value={form.ein || ""} onChange={(e) => setForm({ ...form, ein: e.target.value })} placeholder="12-3456789" className={`${inp} font-mono`} data-testid="carrier-ein" /></Field>
            <Field label="Email"><Input data-testid="carrier-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} /></Field>
            <Field label="Phone"><Input data-testid="carrier-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inp} /></Field>
            <div className="sm:col-span-2"><Field label="Address"><Input data-testid="carrier-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inp} /></Field></div>
            <Field label="Assigned Dispatcher">
              <Select value={form.assigned_dispatcher || "none"} onValueChange={(v) => setForm({ ...form, assigned_dispatcher: v === "none" ? "" : v })}>
                <SelectTrigger className={inp} data-testid="carrier-dispatcher"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Unassigned</SelectItem>{dispatchers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Source"><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className={inp} /></Field>
            <Field label="Payment Method">
              <Select value={form.payment_method || "none"} onValueChange={(v) => setForm({ ...form, payment_method: v === "none" ? "" : v })}>
                <SelectTrigger className={inp} data-testid="carrier-payment-method"><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Dispatch Charges Rate"><Input value={form.dispatch_charges_rate} onChange={(e) => setForm({ ...form, dispatch_charges_rate: e.target.value })} className={inp} placeholder="5% or $150/load" /></Field>
            <Field label="Lead Born Date"><Input type="date" value={(form.lead_born_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, lead_born_date: e.target.value })} className={inp} /></Field>
            <Field label="First Load Date"><Input type="date" value={(form.first_load_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, first_load_date: e.target.value })} className={inp} /></Field>
            <div className="sm:col-span-2"><Field label="Additional Service"><Input value={form.additional_service} onChange={(e) => setForm({ ...form, additional_service: e.target.value })} className={inp} /></Field></div>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className={inp}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </Field>
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Truck size={16} className="text-blue-600" weight="duotone" />Fleet / Equipment</label>
                <span className="text-xs font-bold text-blue-600" data-testid="carrier-total-trucks">Total: {totalTrucks} truck{totalTrucks === 1 ? "" : "s"}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 rounded-lg border border-slate-200 p-3 bg-slate-50">
                {EQUIPMENT_TYPES.map((t) => {
                  const qty = fleetQty(t);
                  return (
                    <div key={t} className={`flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 border ${qty > 0 ? "border-blue-300 bg-white" : "border-transparent"}`}>
                      <span className="text-sm text-slate-700 truncate">{t}</span>
                      <Input type="number" min="0" value={qty || ""} placeholder="0" onChange={(e) => setFleetQty(t, e.target.value)}
                        className="w-14 h-8 text-center bg-white border-slate-300 font-mono" data-testid={`fleet-qty-${t.replace(/\s+/g, "-")}`} />
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="sm:col-span-2">
              <Field label="Notes">
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inp} rows={3}
                  placeholder="Driver John on holiday until Oct 15 · Looking for reefer trailer on rent · Prefers West Coast loads · Avoids NYC…"
                  data-testid="carrier-notes" />
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="save-carrier-btn">{saving ? "Saving…" : editing ? "Save" : "Add Carrier"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
